import { useCallback, useEffect, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useRoomContext } from '@src/contexts';
import SocketService from '@src/services/SocketService';
import { usePeersStore, useRecordingStore, useRoomActionsStore, useRoomStore } from '@src/stores';

import { INFO_MODAL_ICONS } from '@src/constants';

import { ECommonSocketKeys } from '@src/types';
import { IObuttonProps } from '@src/shared/OButton';

const useRecording = () => {
  const { currentRecorder, setCurrentRecorder, onDismiss, onConfirm } = useRecordingStore(
    useShallow((state) => ({
      currentRecorder: state.currentRecorder,
      setCurrentRecorder: state.setCurrentRecorder,
      onDismiss: state.onDismiss,
      onConfirm: state.onConfirm,
    }))
  );

  const { setInfoModalContent, setIsInfoModalOpen, checkboxState, setCheckboxState, isInfoModalOpen } = useRoomStore(
    useShallow((state) => ({
      setIsInfoModalOpen: state.setIsInfoModalOpen,
      setInfoModalContent: state.setInfoModalContent,
      checkboxState: state.checkboxState,
      setCheckboxState: state.setCheckboxState,
      isInfoModalOpen: state.isInfoModalOpen,
    }))
  );

  const { isHostUser } = usePeersStore(
    useShallow((state) => ({
      isHostUser: state.isHostUser,
    }))
  );

  const { roomId } = useRoomStore();
  const { meetingType } = useRoomStore();

  const {
    screenRecordingSettings: {
      isActive: isScreenRecordingActive,
      isDisabled: isScreenRecordingDisabled,
      onClick: onRecordClick,
    },
    setScreenRecordingSettings,
  } = useRoomActionsStore(
    useShallow((state) => ({
      screenRecordingSettings: state.screenRecordingSettings,
      setScreenRecordingSettings: state.setScreenRecordingSettings,
    }))
  );

  const { canUserRecord, currentPeer } = useRoomContext();

  const getStartRecordingButtons = useCallback(
    (checkboxChecked: boolean): IObuttonProps[] => [
      {
        text: 'Cancel',
        variant: 'cancel',
        onClick: () => {
          setIsInfoModalOpen(false);
          setInfoModalContent(null);
        },
      },
      {
        text: 'Start Recording',
        onClick: () => {
          onConfirm();
          // Close immediately after confirming
          setIsInfoModalOpen(false);
          setInfoModalContent(null);
        },
        disabled: !checkboxChecked,
        variant: 'primary',
      },
    ],
    [onConfirm, setInfoModalContent, setIsInfoModalOpen]
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      setCheckboxState(checked);
    },
    [setCheckboxState]
  );

  const stopRecordingButtons: IObuttonProps[] = useMemo(
    () => [
      { text: 'Continue', variant: 'cancel' },
      {
        text: 'Stop Recording',
        onClick: () => {
          onDismiss();
          setCurrentRecorder({ peer_name: '', peer_id: '', action: '' });
          setIsInfoModalOpen(false);
          setInfoModalContent(null);
        },
        variant: 'primary',
      },
    ],
    [onDismiss, setCurrentRecorder, setIsInfoModalOpen, setInfoModalContent]
  );

  useEffect(() => {
    if (!isScreenRecordingActive && currentRecorder.action === 'recording' && isInfoModalOpen) {
      setInfoModalContent((prev) =>
        prev
          ? {
              ...prev,
              checkbox: {
                label: 'I confirm all participants have been notified',
                checked: checkboxState,
                onChange: handleCheckboxChange,
              },
              buttons: getStartRecordingButtons(checkboxState),
            }
          : null
      );
    }
  }, [
    checkboxState,
    currentRecorder.action,
    getStartRecordingButtons,
    handleCheckboxChange,
    isScreenRecordingActive,
    isInfoModalOpen,
    setInfoModalContent,
  ]);

  const handleRecordAction = useCallback(() => {
    if (!isScreenRecordingDisabled) {
      if (!isScreenRecordingActive) {
        setCurrentRecorder({
          peer_name: currentPeer?.peer_name || currentPeer?.peer_info?.peer_name || '',
          peer_id: currentPeer?.id || currentPeer?.peer_info?.peer_id || '',
          action: 'recording',
        });
        // Set modal content BEFORE opening modal to prevent empty modal
        setInfoModalContent({
          icon: INFO_MODAL_ICONS.START_RECORDING,
          title: 'Ready to Start Recording?',
          description: 'Make sure you have obtained permission from all participants before recording the meeting.',
          checkbox: {
            label: 'I confirm all participants have been notified',
            checked: checkboxState,
            onChange: handleCheckboxChange,
          },
          buttons: getStartRecordingButtons(checkboxState),
        });
        onRecordClick(true);
        setIsInfoModalOpen(true);
      } else {
        const data = {
          room_id: roomId,
          peer_name: currentRecorder.peer_name,
          peer_id: currentRecorder.peer_id,
          type: 'recording',
          status: false,
          extraData: { initiator_peer_id: currentPeer?.peer_info?.peer_id || currentPeer?.id },
          broadcast: true,
        };
        if (currentRecorder.peer_name === currentPeer?.peer_info?.peer_name) {
          setInfoModalContent({
            icon: INFO_MODAL_ICONS.STOP_RECORDING,
            title: 'Stop Recording?',
            description: 'The recording will be downloaded automatically once you end the call.',
            buttons: stopRecordingButtons,
          });
          setIsInfoModalOpen(true);
        } else {
          SocketService.emit('updatePeerInfo', data);
          if (meetingType === 'webinar') {
            SocketService.emit(ECommonSocketKeys.RECORDING_ACTION, {
              peer_name: currentRecorder.peer_name || currentPeer?.peer_name || currentPeer?.peer_info.peer_name,
              peer_id: currentRecorder?.peer_id || currentPeer?.id || currentPeer?.peer_info?.peer_id,
              action: 'Stop conference recording',
              status: false,
              initiator_peer_id: currentPeer?.peer_info?.peer_id || currentPeer?.id,
            });
          }
          setScreenRecordingSettings({ isActive: false });
        }
      }
    }
  }, [
    isScreenRecordingDisabled,
    isScreenRecordingActive,
    setCurrentRecorder,
    currentPeer?.peer_name,
    currentPeer?.peer_info.peer_name,
    currentPeer?.peer_info?.peer_id,
    currentPeer?.id,
    setInfoModalContent,
    checkboxState,
    handleCheckboxChange,
    getStartRecordingButtons,
    onRecordClick,
    setIsInfoModalOpen,
    roomId,
    currentRecorder.peer_name,
    currentRecorder.peer_id,
    stopRecordingButtons,
    meetingType,
    setScreenRecordingSettings,
  ]);

  const havePermissionToStopRecording = useMemo(
    () => isScreenRecordingActive && (currentRecorder.peer_name === currentPeer?.peer_info.peer_name || isHostUser),
    [isScreenRecordingActive, currentRecorder.peer_name, currentPeer?.peer_info.peer_name, isHostUser]
  );

  return { handleRecordAction, havePermissionToStopRecording, canUserRecord };
};

export default useRecording;
